// features/news/news.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchNewsBySlugApi, fetchPaginatedNewsApi, searchNewsApi } from './news.service';

export const fetchNewsBySlug = createAsyncThunk(
  'news/fetchNewsBySlug',
  async (payload: any) => {
    const data = await fetchNewsBySlugApi(payload);
    return data;
  }
);

export const fetchPaginatedNews = createAsyncThunk(
  'news/fetchPaginatedNews',
  async (payload: any) => {
    const data = await fetchPaginatedNewsApi(payload);
    return data;
  }
);

export const searchNews = createAsyncThunk(
  'news/searchNews',
  async (payload: any) => {
    const data = await searchNewsApi(payload);
    return data;
  }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type NewsState = {
  message: string | null;
  newsData: Awaited<ReturnType<typeof fetchNewsBySlugApi>> | null;
  selectedNews: any | null;
  paginatedNews: Awaited<ReturnType<typeof fetchPaginatedNewsApi>> | null;
  searchResults: Awaited<ReturnType<typeof searchNewsApi>> | null;
  loading: boolean;
  error: string | null;
};

const initialState: NewsState = {
  message: null,
  newsData: null,
  selectedNews: null,
  paginatedNews: null,
  searchResults: null,
  loading: false,
  error: null,
};

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    clearNewsData: (state) => {
      state.newsData = null;
      state.selectedNews = null;
      state.paginatedNews = null;
      state.searchResults = null;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch news by slug cases
      .addCase(fetchNewsBySlug.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewsBySlug.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.newsData = payload.response || payload;
        state.selectedNews = payload.response || payload;
        state.message = payload.message || 'News fetched successfully!';
      })
      .addCase(fetchNewsBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch news';
      })

      // Fetch paginated news cases
      .addCase(fetchPaginatedNews.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaginatedNews.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.paginatedNews = {
          data: payload.response || [],
          totalCount: payload.totalCount || 0,
          statusCode: payload.statusCode,
          message: payload.message
        };
        state.message = payload.message || 'Paginated news fetched successfully!';
      })
      .addCase(fetchPaginatedNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch paginated news';
      })

      // Search news cases
      .addCase(searchNews.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchNews.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.searchResults = {
          data: payload.response || [],
          totalCount: payload.totalCount || 0,
          statusCode: payload.statusCode,
          message: payload.message,

        };
        state.message = payload.message || 'News search completed successfully!';
      })
      .addCase(searchNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search news';
      });
  },
});

export const { clearNewsData } = newsSlice.actions;
export default newsSlice.reducer;